import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import AppPaths from '../../../../AppPaths';
import { useHistory, useLocation } from 'react-router-dom';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import Button from '../../../../components/Button';
import { inAppCancellation as events } from '../../../../lib/analytics/events';
import { Carousel, CarouselController } from '../../../../components/Carousel';
import { gqlTypes } from '../../../../types';
import { useZendeskCancellationTicket } from '../hooks/useZendesk';
import classNames from 'classnames';
import { petsWithActiveSubscriptionQuery, hasMultidogDiscountQuery } from '../../../../graphql-operations';
import { useQuery } from '@apollo/client';
import lunaStepCountImg from '../../../../assets/images/in_app_cancellation/luna_step_count.png';
import maxLostDogImg from '../../../../assets/images/in_app_cancellation/max_lost_dog_map.png';
import featureIconChartImg from '../../../../assets/images/in_app_cancellation/feature_icon_chart.png';
import featureIconMapMarkerImg from '../../../../assets/images/in_app_cancellation/feature_icon_map_marker.png';
import CancelConfirmationModal from '../../components/CancelConfirmationModal';
import { BaseInAppCancellationState, SubscriptionCategory } from '../util/types';

export default function TooExpensive() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<BaseInAppCancellationState>();
  const { subscriptionCategory } = location.state;
  const { device } = usePurchaseSubscriptionContext();
  const [selectedPageIdx, setSelectedPageIdx] = useState(0);
  const [showNextButton, setShowNextButton] = useState(true);
  const carouselControllerRef = useRef<CarouselController>();
  events.pageLoad({ petId: device.pet!.id, subscriptionCategory }, 'Too Expensive');
  const { mutation, loading } = useZendeskCancellationTicket({
    analyticsEvent: events.dogDoesNotRunAwayCancel,
    selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
    device,
    subscriptionCategory,
  });
  const query = useQuery<gqlTypes.ECOMMERCE_petsWithActiveSubscription>(petsWithActiveSubscriptionQuery, {});
  const multidogDiscountQuery = useQuery<gqlTypes.ECOMMERCE_hasMultidogDiscount>(hasMultidogDiscountQuery, {});
  const showMonthToMonthSpecialOffers = subscriptionCategory === SubscriptionCategory.MONTHLY;

  if (!device.subscription?.id) {
    throw new Error(t('no_recurly_subscription_found'));
  }

  if (loading) {
    return loading;
  }

  const petsWithActiveSubscription = query.data?.petsWithActiveSubscription ?? [];
  const isMultidogHousehold = petsWithActiveSubscription.length > 1;
  const hasMultidogDiscount = multidogDiscountQuery.data?.currentUser.hasMultidogDiscount ?? false;
  const offerMultidogDiscount = isMultidogHousehold && !hasMultidogDiscount;
  const offerFreeMonth = !!device.shouldOfferFreeMonthDuringCancellation;
  const isS3Device = device.currentCollarModel === 'S3';
  const offerS3Downgrade = isS3Device && !!device.shouldOfferDowngradeDuringCancellation;

  const handleKeep = () => {
    events.tooExpensiveKeepMembership({ petId: device.pet?.id, subscriptionCategory });
    history.push({
      pathname: AppPaths.Subscription.KeepMembershipConfirmation(device.moduleId),
      state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE, subscriptionCategory },
      search: window.location.search,
    });
  };

  const handleRetentionOffers = () => {
    events.tooExpensiveContinue({ petId: device.pet?.id, subscriptionCategory });

    if (showMonthToMonthSpecialOffers) {
      // Take S3 monthly users to special offers screen.
      history.push({
        pathname: AppPaths.Subscription.SpecialOffers(device.moduleId),
        state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE, subscriptionCategory },
        search: window.location.search,
      });
    } else if (offerMultidogDiscount) {
      history.push({
        pathname: AppPaths.Subscription.MultidogDiscount(device.moduleId),
        search: window.location.search,
        state: { subscriptionCategory },
      });
    } else if (offerS3Downgrade) {
      history.push({
        pathname: AppPaths.Subscription.DowngradeS3(device.moduleId),
        state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE, subscriptionCategory },
        search: window.location.search,
      });
    } else if (offerFreeMonth) {
      history.push({
        pathname: AppPaths.Subscription.FreeMonthOffer(device.moduleId),
        state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE, subscriptionCategory },
        search: window.location.search,
      });
    } else {
      throw new Error(t('something_went_wrong'));
    }
  };

  const handleCancel = () => {
    events.tooExpensiveContinue({ petId: device.pet?.id, subscriptionCategory });
    mutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription!.id,
          cancellationReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
        },
      },
    });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() =>
            events.tooExpensiveBack({ petId: device.pet?.id, subscriptionCategory }, selectedPageIdx + 1)
          }
          closeButtonAnalyticsEvent={() =>
            events.tooExpensiveClose({ petId: device.pet?.id, subscriptionCategory }, selectedPageIdx + 1)
          }
        />
        <div className={styles.subscriptionContainer}>
          <>
            <div className={styles.imagesCarouselContainer}>
              <Carousel
                cellAlign="center"
                className={styles.imageCarousel}
                pageDots={true}
                controllerRef={carouselControllerRef}
                prevNextButtons={true}
                onChange={(idx) => {
                  idx === 2 ? setShowNextButton(false) : setShowNextButton(true);
                  const analyticsPageIdx = selectedPageIdx + 1;
                  if (idx > selectedPageIdx) {
                    events.tooExpensiveNext({ petId: device.pet?.id, subscriptionCategory }, analyticsPageIdx);
                  } else if (idx < selectedPageIdx) {
                    events.tooExpensiveBack({ petId: device.pet?.id, subscriptionCategory }, analyticsPageIdx);
                  }
                  setSelectedPageIdx(idx);
                }}
              >
                <div className={styles.imageCarouselImage}>
                  <div className={styles.largeHeader}>
                    <h1>{t('health_monitoring_saved_luna')}</h1>
                  </div>
                  <img
                    className={styles.stepCount}
                    src={lunaStepCountImg}
                    alt={t('a_bar_graph_showing_declining_step_count_day_over_day')}
                  />
                  <p>
                    <strong>{t('fi_can_tell_you_more_than_just_dog_location')}</strong>
                    <br />
                    {t(
                      'active_frenchie_luna_had_taken_a_20000_step_plunge_in_activity_and_a_quick_trip_to_the_vet_confirmed_what_the_data_was_suggesting_luna_had_swallowed_a_rock_data_from_fi_led_to_the_intervention_that_saved_her_life',
                    )}
                  </p>
                </div>
                <div className={classNames(styles.imageCarouselImage, styles.xray)}>
                  <div className={styles.largeHeader}>
                    <h1>{t('lost_dog_mode_saved_max')}</h1>
                  </div>
                  <img src={maxLostDogImg} alt={t('map_showing_a_lost_dog')} />
                  <p>
                    <strong>{t('because_every_second_counts')}</strong>
                    <br />
                    {t(
                      'german_shepherd_max_spotted_a_squirrel_and_took_off_his_owner_matt_immediately_put_fi_into_lost_dog_mode_and_found_max_in_a_nearby_creek_without_fi_matt_wouldnt_have_found_him_so_fast_or_maybe_at_all',
                    )}
                  </p>
                </div>
                <div className={styles.imageCarouselImage}>
                  <div className={styles.largeHeader}>
                    <h1>{t('a_dogs_health_and_safety_will_always_be_our_1_priority')}</h1>
                  </div>
                  <div className={styles.featureDescription}>
                    <img src={featureIconChartImg} alt={t('chart_icon')} className={styles.icon} />
                    <p>
                      <strong>{t('full_health_monitoring')}</strong>{' '}
                      {t(
                        'monitor_your_dogs_activity_and_sleep_soon_youll_be_able_to_also_track_advanced_behaviors_like_licking_that_offer_deeper_insight_into_your_dogs_health',
                      )}
                    </p>
                  </div>
                  <div className={styles.featureDescription}>
                    <img src={featureIconMapMarkerImg} alt={t('map_marker_icon')} className={styles.icon} />
                    <p>
                      <strong>{t('lost_dog_mode')}</strong>{' '}
                      {t(
                        'when_your_dog_goes_missing_now_you_can_track_every_step_in_real_time_on_the_map_to_make_recovery_even_faster',
                      )}
                    </p>
                  </div>
                  <div className={styles.featureDescription}>
                    <p className={styles.finalThought}>
                      {t('hundreds_of_thousands_of_dog_parents_trust_fi_to_be_there_when_it_counts')}
                    </p>
                  </div>
                </div>
              </Carousel>
            </div>
          </>
        </div>
        <div className={styles.buttonContainer}>
          {showNextButton ? (
            <>
              <Button
                className={styles.button}
                onClick={() => {
                  const temp: any = document.querySelector('.flickity-button.flickity-prev-next-button.next');
                  temp.click();
                }}
              >
                {t('next')}
              </Button>
              {/* Spacer ensures primary button is aligned with buttons on neighboring pages in in-app cancel flow */}
              <div className={styles.buttonSpacer}></div>
            </>
          ) : (
            <>
              <Button className={styles.button} onClick={handleKeep}>
                {t('keep_membership')}
              </Button>

              {showMonthToMonthSpecialOffers || offerS3Downgrade || offerFreeMonth || offerMultidogDiscount ? (
                <Button onClick={handleRetentionOffers} className={styles.button} tertiary={true}>
                  {t('continue_with_cancellation')}
                </Button>
              ) : (
                <CancelConfirmationModal
                  onContinue={() => handleCancel()}
                  pageName={t('too_expensive')}
                  petId={device.pet!.id}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
